import React, { Suspense } from 'react'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import '@/index.css'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { Route, Routes } from 'react-router-dom'
import { appRoutes } from './constants.js'
import { ScrollToTop } from './components/ScrollToTop.js'
import { getConfig } from './config.js'
import ErrorPage from './components/ErrorPage.js'
import QuestLoader from './components/QuestLoader.js'

import Home from '@/views/Home.js'

const AutoLockPlanner = React.lazy(() => import('./views/AutoLockPlanner.js'))
const ManualLockPlanner = React.lazy(
  () => import('./views/ManualLockPlanner.js')
)
const NotFound = React.lazy(() => import('@/views/NotFound.js'))
const Vault = React.lazy(() => import('@/views/Vault.js'))
const ViewPassword = React.lazy(() => import('./views/ViewPassword.js'))
const UnlockLock = React.lazy(() => import('./views/UnlockLock.js'))
const HowItWorks = React.lazy(() => import('./views/HowItWorks.js'))
const PasswordGeneratorTool = React.lazy(
  () => import('./views/PasswordGenerator.js')
)
const ContactUsPage = React.lazy(() => import('./views/Contact.js'))
const PrivacyPolicy = React.lazy(() => import('./views/PrivacyPolicy.js'))
const TermsOfService = React.lazy(() => import('./views/TermsOfService.js'))

const Auth0ProviderWithHistory = ({
  children
}: {
  children: React.ReactNode
}) => {
  const navigate = useNavigate()
  const config = getConfig()

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    const targetPath = appState?.returnTo ? appState.returnTo : '/'
    navigate(targetPath, { replace: true })
  }

  const providerConfig = {
    domain: config.domain,
    clientId: config.clientId,
    onRedirectCallback,
    authorizationParams: {
      redirect_uri: window.location.origin,
      ...(config.audience ? { audience: config.audience } : null)
    }
  }

  return <Auth0Provider {...providerConfig}>{children}</Auth0Provider>
}

const AppRoutes = () => {
  const { error } = useAuth0()

  if (error) {
    return <ErrorPage showBackButton={false} />
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<QuestLoader />}>
        <Routes>
          <Route path={appRoutes.home}>
            <Route index path={appRoutes.home} element={<Home />} />
            <Route
              path={appRoutes.autoLockPlanner}
              element={<AutoLockPlanner />}
            />
            <Route
              path={appRoutes.manualLockPlanner}
              element={<ManualLockPlanner />}
            />
            <Route path={appRoutes.howItWorks} element={<HowItWorks />} />
            <Route path={appRoutes.vault} element={<Vault />} />
            <Route path={appRoutes.viewPassword} element={<ViewPassword />} />
            <Route path={appRoutes.unlockLock} element={<UnlockLock />} />
            <Route
              path={appRoutes.passwordGenerator}
              element={<PasswordGeneratorTool />}
            />
            <Route path={appRoutes.contact} element={<ContactUsPage />} />
            <Route path={appRoutes.privacyPolicy} element={<PrivacyPolicy />} />
            <Route
              path={appRoutes.termsOfService}
              element={<TermsOfService />}
            />
            <Route path={appRoutes.notFound} element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

const App = () => {
  return (
    <BrowserRouter basename="/">
      <Auth0ProviderWithHistory>
        <AppRoutes />
      </Auth0ProviderWithHistory>
    </BrowserRouter>
  )
}

export default App
